import { DefaultCustomField } from 'src/constants/types';
import { CampaignConfigSchema, CampaignConfigType } from '../types';
import { SelectOption } from '../components/types';

/**
 * Identifier for a type-specific configuration section.
 * Each key maps to one presentational section component.
 */
export type SectionKey =
  | 'groups'
  | 'keywords'
  | 'appointmentType'
  | 'contactDate'
  | 'recurringSettings'
  | 'textToWinSettings'
  | 'enrollmentWindow';

/**
 * Shared props every section component receives. A section reads its slice of
 * the config and reports changes through `onConfigUpdate` (partial merge).
 */
export interface SectionProps {
  config: CampaignConfigSchema;
  onConfigUpdate: (updates: Partial<CampaignConfigSchema>) => void;
  options?: {
    groups?: SelectOption[];
    keywords?: SelectOption[];
    appointmentTypes?: SelectOption[];
    contactDateTypes?: SelectOption[];
  };
  showValidation?: boolean;
  selected?: boolean;
  onSelect?: () => void;
  /** Date-typed custom fields, for TemplateText macro support. */
  customFields?: DefaultCustomField[];
}

export interface TypeSectionConfig {
  /** Type-specific sections rendered (in order) for this campaign type. */
  sections: SectionKey[];
  /** Whether the message-templates editor is shown for this type. */
  hasMessageTemplates: boolean;
}

/**
 * Single source of truth mapping each campaign type to the sections it renders.
 * Adding a new campaign type = add one row here + one section component.
 * Visibility/required defaults on type-change live in helpers/configDefaults
 * (applyTypeDefaults), so they are not duplicated here.
 */
export const SECTION_REGISTRY: Record<CampaignConfigType, TypeSectionConfig> = {
  message: { sections: ['groups'], hasMessageTemplates: true },
  join: { sections: ['groups', 'keywords'], hasMessageTemplates: true },
  enrollment: { sections: ['groups', 'keywords', 'enrollmentWindow'], hasMessageTemplates: true },
  reminder: { sections: ['appointmentType'], hasMessageTemplates: true },
  annual: { sections: ['groups', 'contactDate'], hasMessageTemplates: true },
  recurring: { sections: ['groups', 'recurringSettings'], hasMessageTemplates: true },
  // text_to_win owns its message copy inside the settings section — no scheduled messages.
  text_to_win: { sections: ['keywords', 'textToWinSettings'], hasMessageTemplates: false },
  away_message: { sections: [], hasMessageTemplates: true },
  custom: { sections: [], hasMessageTemplates: false },
};

export const getSectionsForType = (type: CampaignConfigType): SectionKey[] =>
  SECTION_REGISTRY[type]?.sections ?? [];

export const typeHasMessageTemplates = (type: CampaignConfigType): boolean =>
  SECTION_REGISTRY[type]?.hasMessageTemplates ?? false;
